<template>
    <overlay
        @overlay-click="close()"
    >
        <card-component
            :title="'お問い合わせ'"
            class="shadow-lg w-full md:w-3/5 lg:w-2/5 z-50"
            :header-icon="mdiClose"
            @header-icon-click="close()"
        >
            <div
                class="mb-5 border-b border-gray-300 dark:border-gray-900"
            >
                <div class="space-y-3 mb-5">
                    <form
                        v-if="isSubmit"
                        @submit.prevent
                    >
                        <field confirm="お問い合わせを送信しますか？">
                            <div class="text-sm md:text-base flex flex-col justify-center px-4 md:px-16 pb-8 font-medium">
                                <table class="table-auto w-full">
                                    <tbody>
                                        <tr class="border-b">
                                            <td class="px-4 py-2 w-1/3">
                                                種別
                                            </td>
                                            <td class="px-4 py-2">
                                                {{ inquiry.category }}
                                            </td>
                                        </tr>
                                        <tr
                                            v-if="inquiry.pageInfo"
                                            class="border-b"
                                        >
                                            <td class="px-4 py-2 w-1/3">
                                                ページ・画面
                                            </td>
                                            <td class="px-4 py-2">
                                                {{ inquiry.pageInfo }}
                                            </td>
                                        </tr>
                                        <tr
                                            v-if="inquiry.deviceInfo"
                                            class="border-b"
                                        >
                                            <td class="px-4 py-2 w-1/3">
                                                使用端末情報
                                            </td>
                                            <td class="px-4 py-2">
                                                {{ inquiry.deviceInfo }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div class="flex w-full">
                                    <div class="px-4 py-2 w-1/3">
                                        内容
                                    </div>
                                    <span class="pl-4 py-2 w-2/3 text-ellipsis">
                                        {{ inquiry.inquiry }}
                                    </span>
                                </div>
                            </div>
                        </field>
                    </form>
                    <form
                        v-else
                        @submit.prevent
                    >
                        <field label="種別※">
                            <control
                                v-model="inquiry.category"
                                required
                                :type="'select'"
                                :options="inquirySelectOptions"
                            />
                        </field>
                        <field
                            v-if="isShowPageInfoForm"
                            label="ページ・画面"
                        >
                            <control
                                v-model="inquiry.pageInfo"
                                :type="'text'"
                                placeholder="トレーニング動画・測定入力画面など"
                                :maxlength="100"
                            />
                        </field>
                        <field
                            v-if="isShowDeviceInfoForm"
                            label="使用端末情報"
                        >
                            <control
                                v-model="inquiry.deviceInfo"
                                :type="'text'"
                                placeholder="端末情報を入力してください：iOS 16 / android / タブレットなど"
                                :maxlength="100"
                            />
                        </field>
                        <field label="内容※">
                            <control
                                v-model="inquiry.inquiry"
                                type="textarea-bg-white"
                                required
                                placeholder="お問い合わせ内容を入力してください"
                                :maxlength="1000"
                            />
                        </field>
                    </form>
                </div>
            </div>
            <jb-buttons
                class="grid grid-cols-2 gap-3"
            >
                <div class="flex">
                    <jb-button
                        :label="isSubmit ? 'もどる' : 'キャンセル'"
                        color=""
                        class="bg-white text-[#00AF5B] border-[#00AF5B] w-1/2"
                        outline
                        @click="isSubmit ? isSubmit = false : close()"
                    />
                </div>
                <div class="flex justify-end">
                    <jb-button
                        :label="isSubmit ? '送信する' : '確認'"
                        :disabled="!username || !inquiry?.category || !inquiry?.inquiry"
                        color=""
                        class="bg-[#00AF5B] text-white border-[#00AF5B] w-2/3"
                        :class="!username || !inquiry?.category || !inquiry?.inquiry ?
                            'bg-gray-400 border-gray-400' : ''"
                        @click="isSubmit ? submit(): isSubmit = true"
                    />
                </div>
            </jb-buttons>
        </card-component>
    </overlay>
</template>

<script setup>
import { ref, reactive, watch, inject, } from 'vue'
import { useStore } from 'vuex'
import { mdiClose } from '@mdi/js'
import base from '@/api/base'
import JbButton from '@/components/Utilities/JbButton'
import JbButtons from '@/components/Utilities/JbButtons'
import CardComponent from '@/components/Utilities/CardComponent'
import Overlay from '@/components/Utilities/Overlay'
import Control from '@/components/Utilities/Control'
import Field from '@/components/Utilities/Field'

const store = useStore()
const toast = inject('$toast')

const emit = defineEmits(['close', 'submit'])

const inquirySelectOptions = [
    { name: '選択してください。▼', value: '' },
    { name: 'トレーニング動画・セット数について', value: 'トレーニング動画・セット数について' },
    { name: '測定結果・目標設定について', value: '測定結果・目標設定について' },
    { name: 'その他操作方法について', value: 'その他操作方法について' },
    { name: '不具合について', value: '不具合について' },
    { name: 'ご意見・ご要望', value: 'ご意見・ご要望' },
    { name: 'その他', value: 'その他' },
]

const username = ref(store.state.userName)

const inquiry = reactive({
    category: '',
    pageInfo: '',
    deviceInfo: '',
    inquiry: '',
})

const isSubmit = ref(false)
const isShowPageInfoForm = ref(false)
const isShowDeviceInfoForm = ref(false)

const resetInquiryForm = () => {
    inquiry.category = ''
    inquiry.pageInfo = ''
    inquiry.deviceInfo = ''
    inquiry.inquiry = ''
}

const close = () => {
    emit('close')
    isSubmit.value = false
}

const submit = async() => {
    try {
        const res = await base.post('inquiry', inquiry, true, true)
        if (res) {
            toast('お問い合わせを受け付けました。後日、運営よりご回答いたします', {
                type: 'success'
            })
            emit('submit')
            resetInquiryForm()
        }
    } catch (e) {
        console.log("問い合わせエラー")
        console.error(e)
        toast(e.message, {
            type: 'danger'
        })
    }
}

watch(() => inquiry, (newVal) => {
    isShowPageInfoForm.value =
        newVal.category === 'その他操作方法について' ||
        newVal.category === '不具合について'
    isShowDeviceInfoForm.value =
        newVal.category === 'トレーニング動画・セット数について' ||
        newVal.category === 'その他操作方法について' ||
        newVal.category === '不具合について'
}, { deep: true })
</script>
